import React , {useState}  from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { FiFileText, FiCheckCircle, FiShield, FiUser } from "react-icons/fi";
import * as api from "../../api/index";

const Contractpayment = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Data passed from Create Contract page (supports both 'amount' / 'price' keys)
  const contract = state || {
    itemTitle: "No Item",
    itemDescription: "",
    myName: "",
    myEmail: "",
    otherPartyName: "Unknown",
    otherPartyEmail: "",
    amount: 0,
    price: 0,
  };

  const contractAmount = Number(contract.amount || contract.price || 0);

const handlePaystack = async () => {
    if (contractAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.initializeContractPayment({
        ...contract,
        price: contractAmount,
      });

      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, "_blank");
      } else {
        setError("Failed to get Paystack checkout URL.");
      }
    } catch (err) {
      console.error(err);
      setError("Payment initialization failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }; 

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* 🔝 Sticky Header matching Contract.jsx */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Contract Payment</h1>
        <div className="w-16" /> {/* Spacer for symmetry */}
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        
        {/* 🛡️ Info Banner matching Contract.jsx style */}
        <div className="bg-teal-900 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-teal-100 overflow-hidden relative">
          <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
               <div className="p-2 bg-teal-800 rounded-xl"><FiShield className="text-teal-100" /></div>
               <h2 className="text-sm font-black uppercase tracking-widest">OurASAP Secure Checkout</h2>
            </div>
            <div className="space-y-3">
              {[
                "Your payment is securely processed through Paystack",
                "Contract becomes active immediately after confirmation",
                "Automated digital copies sent to both parties"
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3 text-xs font-bold text-teal-50">
                  <FiCheckCircle className="text-teal-400 shrink-0" /> {text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 💳 Payment Details Container Card */}
        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
          <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <FiFileText className="text-orange-600" /> Transaction Summary
          </h3>

          <div className="space-y-4 text-sm font-bold">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Item</span>
              <span className="text-gray-900 text-right">{contract.itemTitle}</span>
            </div>

            <div className="flex justify-between items-start py-3 border-b border-gray-100 gap-4">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest shrink-0">Description</span>
              <span className="text-gray-900 text-right font-medium text-xs line-clamp-2 max-w-[240px]">
                {contract.itemDescription}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">My Name</span>
              <span className="text-gray-900 text-right">{contract.myName || "N/A"}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">My Email</span>
              <span className="text-gray-900 text-right">{contract.myEmail || "N/A"}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Other Party</span>
              <span className="text-gray-900 text-right">{contract.otherPartyName}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Other Party Email</span>
              <span className="text-gray-900 text-right">{contract.otherPartyEmail}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Contract Amount</span>
              <span className="text-gray-900 font-black">₦{contractAmount.toLocaleString()}</span>
            </div>

            <div className="flex justify-between items-center pt-2">
              <span className="text-sm font-black text-gray-900 uppercase tracking-wider">Total Due</span>
              <span className="text-2xl font-black text-orange-600">₦{contractAmount.toLocaleString()}</span>
            </div>
          </div>

          {/* Paystack Button */}
          <button
            onClick={handlePaystack}
            className="w-full py-5 bg-gray-900 hover:bg-black text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-sm transition-all shadow-xl shadow-gray-300 active:scale-95 flex items-center justify-center gap-3 mt-6"
          >
            <FiCheckCircle size={18} className="text-[#00C853]" />
            Pay ₦{contractAmount.toLocaleString()} with Paystack
          </button>

          <p className="text-center text-[10px] font-black text-gray-400 uppercase tracking-widest pt-2">
            Secured via Paystack • Instant Activation
          </p>
        </div>

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Contracts • Instant Enforcement • OurASAP
        </p>
      </div>
    </div>
  );
};

export default Contractpayment;